import React, { memo, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Button from "components/button/Button";
/**
 * PopUpButton 컴포넌트: 버튼을 클릭하면 Popover를 표시하고,
 * 외부 클릭 시 Popover를 닫는 기능을 제공하는 컴포넌트입니다.
 *
 * @param {Object} props - 컴포넌트에 전달되는 props
 * @param {React.ReactNode} props.children - Popover 안에 들어갈 내용
 * @param {string} props.text - 버튼에 표시할 텍스트
 * @param {React.ReactNode} props.icon - 버튼에 표시할 아이콘
 * @param {string} props.className - 추가할 클래스 이름
 * @param {number} props.top - Popover의 상단 위치
 * @param {number} props.left - Popover의 좌측 위치
 * @param {Function} props.onClick - 버튼 클릭 시 실행할 콜백 함수
 * @returns {React.ReactElement}
 */
const PopUpButton = ({
  isopen,
  children,
  text,
  icon,
  className,
  top = 0,
  left = 0,
  onClick,
  onClose,
}) => {
  const [popoverPosition, setPopoverPosition] = useState({
    top: 0,
    left: 0,
  }); // Popover의 위치 관리
  const buttonRef = useRef(null); // 버튼의 DOM 요소 참조

  /**
   * Popover 위치 업데이트 함수
   * @param {number} top - Popover의 상단 위치
   * @param {number} left - Popover의 좌측 위치
   */
  const updatePopoverPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect(); // 버튼의 위치 정보 가져오기
      setPopoverPosition({
        top: rect.bottom + 5, // top 값이 없으면 기본 위치로 설정
        left: rect.left, // left 값이 없으면 기본 위치로 설정
      });
    }
  };

  /**
   * Popover 열기/닫기 및 onClick 이벤트 실행
   * @param {number} top - Popover의 상단 위치
   * @param {number} left - Popover의 좌측 위치
   */
  const togglePopover = () => {
    if (!isopen) {
      updatePopoverPosition();
    }
  };

  /**
   * 외부 클릭을 감지하여 Popover 닫기
   * @param {Event} event - 클릭 이벤트 객체
   */
  const handleClickOutside = (event) => {
    if (buttonRef.current && !buttonRef.current.contains(event.target)) {
      onClose();
    }
  };

  /**
   * Popover 열림 상태에 따라 외부 클릭 이벤트 리스너 추가/제거
   */
  useEffect(() => {
    if (isopen) {
      document.addEventListener("click", handleClickOutside); // Popover가 열리면 클릭 이벤트 리스너 추가
    } else {
      document.removeEventListener("click", handleClickOutside); // Popover가 닫히면 클릭 이벤트 리스너 제거
    }

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isopen]);

  const handleClick = (event) => {
    event.stopPropagation(); // 이벤트 전파를 막아 클릭이 외부로 전파되지 않도록 함
    togglePopover(); // Popover 열기/닫기

    if (onClick) {
      onClick(); // 전달된 onClick 이벤트 실행
    }
  };

  return (
    <Container className={className} ref={buttonRef}>
      {/* PopUp 버튼 */}
      <PopUpBtn onClick={handleClick}>
        {text && text} {/* 텍스트가 있을 경우 출력 */}
        {icon && <>{icon}</>} {/* 아이콘이 있을 경우 출력 */}
      </PopUpBtn>

      {/* Popover 영역 */}
      {isopen && (
        <Popover
          top={top ? top : popoverPosition.top}
          left={left ? left : popoverPosition.left}
        >
          {children} {/* Popover 안에 들어갈 내용 */}
        </Popover>
      )}
    </Container>
  );
};

export default memo(PopUpButton);

// PopUpButton을 감싸는 컨테이너 스타일 정의
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  // 각 조건에 따른 스타일 처리
  ${({ className }) => popUpClassName[className] || ""}
`;

// PopUp 버튼 스타일 정의
const PopUpBtn = styled(Button)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  color: ${({ theme }) => theme.color.white};
  cursor: pointer;
`;

// Popover 스타일 정의
const Popover = styled.div`
  position: absolute;
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
  z-index: 1000;
`;

const popUpClassName = {};
